import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

/**
 * Recharts-based visualizations for video analytics (ADR 0005), themed via the
 * design-token CSS variables so they track light/dark automatically.
 */

const fmtTime = (s: number): string => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};

const pct = (v: number): string => `${Math.round(v * 100)}%`;

const axisTick = { fill: "var(--muted-foreground)", fontSize: 11 };

export interface RetentionChartPoint {
    secondsOffset: number;
    reachedFraction: number;
}

/** Audience-retention curve: % of views still watching over the video timeline. */
export function RetentionChart({ points, height = 220 }: { points: RetentionChartPoint[]; height?: number }) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                <defs>
                    <linearGradient id="retentionFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="var(--primary)" stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="secondsOffset" tickFormatter={fmtTime} tick={axisTick} stroke="var(--border)" />
                <YAxis
                    domain={[
                        0,
                        1,
                    ]}
                    tickFormatter={pct}
                    tick={axisTick}
                    stroke="var(--border)"
                    width={40}
                />
                <Tooltip
                    formatter={(v) => [
                        pct(Number(v)),
                        "Still watching",
                    ]}
                    labelFormatter={(label) => `At ${fmtTime(Number(label))}`}
                    contentStyle={{
                        background: "var(--background)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                    }}
                />
                <Area
                    type="monotone"
                    dataKey="reachedFraction"
                    stroke="var(--primary)"
                    strokeWidth={2}
                    fill="url(#retentionFill)"
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}

export interface ReplayHeatmapPoint {
    secondsOffset: number;
    relativeIntensity: number;
}

/** Most-replayed heatmap: replay intensity across the video timeline. */
export function ReplayHeatmap({ points, height = 160 }: { points: ReplayHeatmapPoint[]; height?: number }) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                <XAxis dataKey="secondsOffset" tickFormatter={fmtTime} tick={axisTick} stroke="var(--border)" />
                <YAxis
                    domain={[
                        0,
                        1,
                    ]}
                    tickFormatter={pct}
                    tick={axisTick}
                    stroke="var(--border)"
                    width={40}
                />
                <Tooltip
                    formatter={(v) => [
                        pct(Number(v)),
                        "Replay intensity",
                    ]}
                    labelFormatter={(label) => `At ${fmtTime(Number(label))}`}
                    contentStyle={{
                        background: "var(--background)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                    }}
                />
                <Bar
                    dataKey="relativeIntensity"
                    fill="var(--primary)"
                    radius={[
                        2,
                        2,
                        0,
                        0,
                    ]}
                />
            </BarChart>
        </ResponsiveContainer>
    );
}
